import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import { QueryKeys } from 'src/constants/queryKeys';

import { useAddress } from '@thirdweb-dev/react';
import axios from 'axios';
import qs from 'qs';
import { useConfig } from '@app/config';

export type MerklUserRewards = Record<
  string,
  {
    accumulated: string;
    decimals: number;
    symbol: string;
    unclaimed: string;
    reasons: Record<
      string,
      {
        accumulated: string;
        unclaimed: string;
      }
    >;
    proof: string[];
  }
>;

const fetcher = async (
  address: string | undefined,
  pool: string,
  apiUrl: string | undefined,
  chainIds: number[]
) => {
  if (!address || !pool || !apiUrl || !chainIds.length) {
    return;
  }

  const res = await Promise.all(
    chainIds.map(async chainId => {
      const queryParams = {
        mainParameter: pool,
        user: address,
        chainId: chainId
      };

      const query = qs.stringify(queryParams, {
        encodeValuesOnly: true
      });

      const { data } = await axios.get(`${apiUrl}/userRewards?${query}`);

      return data;
    })
  );

  return res.reduce((r, item) => {
    r = {
      ...r,
      ...item
    };

    return r;
  }, {});
};

export const useMerklUserRewardsByPool = (
  pool: string,
  chainIds: number[],
  options: UseQueryOptions<MerklUserRewards> = {}
) => {
  const address = useAddress();

  const config = useConfig();
  const apiUrl = config?.URLS.merklApiUrl;

  return useQuery<MerklUserRewards>(
    [QueryKeys.MERKL_USER_REWARDS, address, pool, apiUrl, chainIds.join(',')],
    async () => {
      return await fetcher(address, pool, apiUrl, chainIds);
    },
    {
      staleTime: 60000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: !!(pool && config && config.MERKL.DISTRIBUTOR && apiUrl),
      ...options
    }
  );
};
